<template>
  <div class="editor" :data-activeLang="activeLang">
    <!-- module list -->
    <div class="moduleList">
      <div class="tabs">
        <button class="tab toCapitalize"
          v-for="(button, index) in moduleTab"
          :key="index"
          :data-module_type="button.type"
          :class="{ active: button.type === moduleType, f_medium: button.type === moduleType }"
          @click="onModuleTab(button.type)">
          {{ button.label }}
        </button>
      </div>
      <draggable
        class="list"
        :list="moduleList"
        :group="{ name: 'moduleGroup', pull: 'clone', put: false }"
        :sort="false"
        :clone="onClone"
        :filter="'.disabled'"
        :disabled="blockQuantity">
        <div
          v-for="(mods, index) in moduleList"
          :key="`module${index}`"
          :data-mods_name="mods.name"
          :class="{item: true, disabled: (hasQuantity && mods.name === 'quantity') || (hasCert && mods.name === 'cert')}"
          @mouseenter="onCheckQuantityEnter"
          @mouseleave="onCheckQuantityLeave">
          <div class="label"><span>
          {{locale[mods.moduleName]}}
          </span></div>
        </div>
      </draggable>
    </div>
    <!-- //module list -->
    <!-- {{subOption}} -->
    <!-- content -->
    <div class="preview"
      @click="onDocumentClick">
      <div id="article" class="article">
        <span class="previewOnly top">{{locale.editor_preview_area}}</span>
        <div v-if="moduleData.length <= 0" class="requiredMessage">{{locale.editor_select_module}}</div>
        <draggable
          class="modsWrap"
          v-model="moduleData"
          :animation="200"
          :options="{draggable: '.mods'}"
          :style="setFont"
          group="moduleGroup"
          handle=".handleMods .handleMove"
          @change="onChange">
          <div class="mods"
             v-for="(mods, index) in moduleData"
            :key="`mods_${mods.id}`"
            :class="{ghost: mods.hide}"
            :data-fullsize="mods.setting.layout.fullsize && mods.setting.layout.fullsize.active"
            :data-fullsize_align="mods.setting.layout.fullsize && mods.setting.layout.fullsize.active ? mods.setting.layout.fullsize.alignItems || 'center' : 'center'"
            @mouseenter="onMouseEnter"
            @mouseleave="onMouseLeave"
            @click="onModuleClick($event, index, mods)">
            <Module
              :moduleList="moduleList"
              :moduleListAll="moduleListAll"
              :activeLang="activeLang"
              :moduleIndex="index"
              :id="mods.id"
              :isNew="isNew"
              :itemData="mods.dataSet"
              :itemSetting="mods.setting"
              :hasPrice="hasPrice"
              :subOption="subOption"
              @setDetail="onSetDetail"
              @changeColumn="onChangeColumn">
            </Module>
            <div class="responsiveMods">
              <div v-if="responsive === 'mobile'" class="icoHide">
                <input type="checkbox" :id="`hide_${mods.id}_mobile`" :checked="mods.hide" @change="setHide($event, index)">
                <label :for="`hide_${mods.id}_mobile`" class="ico"></label>
              </div>
              <div v-if="responsive === 'tablet'" class="icoHide">
                <input type="checkbox" :id="`hide_${mods.id}_tablet`" :checked="mods.hide" @change="setHide($event, index)">
                <label :for="`hide_${mods.id}_tablet`" class="ico"></label>
              </div>
              <button type="button" class="ico icoCopyto" :data-copyto="responsive" @click="copyToDevice(index)"><span class="blind">copy to</span></button>
            </div>
            <div class="handleMods">
              <button type="button" class="handle handleMove" title="move"></button>
              <button type="button" class="handle handleCopy" @click="onCopy($event, index, mods)" title="copy"></button>
              <button type="button" class="handle handleDel" @click="onRemove($event, index)" title="remove"></button>
            </div>
          </div>
        </draggable>
        <span class="previewOnly">{{locale.editor_preview_area}}</span>
      </div>
    </div>
    <!-- //content -->
    <!-- setting module -->
    <SettingModule
      :locale="locale"
      :isNew="isNew"
      :mId="activeModule.id"
      :activeLang="activeLang"
      :itemModule="itemModule"
      :type="activeModule.type"
      :name="activeModule.name"
      :isActive="isActive"
      :settingTarget="activeTarget"
      :settingData="activeModule.setting"
      :columnData="activeModule.dataSet"
      :detailData="activeColumn"
      :hasQuantity="hasQuantity"
      :hasPrice="hasPrice"
      :linkedProduct="linkedProduct"
      :subOption="subOption"
      @addOption="onAddOption"
      @removeOption="onRemoveOption"
      @checkedPrice="onCheckedPrice">
    </SettingModule>
    <!-- //setting module -->
  </div>
</template>

<script>
import UNIQUE from '@/common/unique'
import modules from './ModuleData'
import draggable from 'vuedraggable'
import Module from './Module.vue'
import SettingModule from './SettingModule.vue'

const MODULE_DATA = Object.values(modules)

export default {
  props: [
    'locale',
    'activeLang',
    'isNew',
    'itemModule',
    'responsive',
    'displayHideModes',
    'linkedProduct'
  ],
  components: {
    draggable,
    Module,
    SettingModule
  },
  computed: {
    dreamAuth () {
      const checkinInfo = this.$store.state.setting.checkinInfo
      return checkinInfo && checkinInfo.selfAuth === 'dream'
    },
    moduleTab () {
      const tab = [
        { type: 'standard', label: this.locale.editor_module_standard },
        { type: 'action', label: this.locale.eidtor_module_action }
      ]
      return tab
    },
    moduleData: {
      get () {
        return this.itemModule && this.itemModule.length ? this.itemModule : []
      },
      set (data) {
        this.$emit('update', data)
      }
    },
    setFont () {
      const font = this.$store.state.setting.font
      const fontFamily = [...new Set([
        font.default,
        font.default2,
        font.default3
      ].filter(Boolean))].join(', ')

      return {
        fontFamily
      }
    },
    hasCert () {
      const result = this.moduleData.find(x => x.name === 'cert') !== undefined
      return result
    },
    hasQuantity () {
      const result = this.moduleData.find(x => x.name === 'quantity') !== undefined
      return result
    },
    hasPrice () {
      const quantity = this.moduleData.find(x => x.name === 'quantity')
      if (this.hasQuantity) {
        return quantity.setting.action.price.isPrice
      } else {
        return false
      }
    },
    columnLength () {
      return this.activeModule.dataSet.column.length
    },
    subOption () {
      const name = /radio|checkbox|select/
      const options = this.itemModule.filter(x => x.type === 'action' && x.setting.action && name.test(x.setting.action.name)).map(x => {
        return {
          id: x.id,
          item: x.setting.action.item,
          label: x.setting.action.labelValue
        }
      }).filter(x => x.item.filter(o => o.subOption && o.subOption !== '').length > 0)
      function getSubOption (arr) {
        let result = []
        for (let i = 0; i < arr.length; i++) {
          const item = arr[i]
          for (let j = 0; j < item.item.length; j++) {
            const opt = item.item[j]
            if (opt.subOption && opt.subOption !== '') {
              result.push({
                parent: item.id,
                parentLabel: item.label,
                parentValue: opt.value,
                child: opt.subOption
              })
            }
          }
        }
        return result
      }
      return getSubOption(options)
    }
  },
  data () {
    return {
      moduleType: 'standard',
      moduleListAll: [],
      moduleList: [],
      editMode: {
        state: 'default'
      },
      blockQuantity: false,
      activeModule: {},
      activeColumn: {},
      activeTarget: '',
      isActive: false,
      // hasQuantity: false,
      checkedPrice: false
    }
  },
  watch: {
    /*
    hasQuantity (hasQuantity) {
      if (!hasQuantity) {
        this.moduleData.filter(m => m.name === 'button').forEach(function (x) {
          x.dataSet.column.forEach(function (c) {
            console.log(c)
          })
        })
        console.log(this.moduleData.filter(m => m.name === 'button'))
      }
    }
    */
    itemModule: {
      deep: true,
      handler (data) {
        const activeModule = this.activeModule.id
        if (data.find(x => x.id === activeModule)) {
          this.activeModule = data.find(x => x.id === activeModule)
        }
      }
    }
  },
  mounted: function () {
    this.getModulesAll()
    this.getModules(this.moduleType)
  },
  methods: {
    onModuleTab: function (type) {
      this.moduleType = type
      this.getModules(type)
    },
    getModulesAll: function () {
      this.moduleListAll = this._.cloneDeep(MODULE_DATA)
    },
    getModules: function (type) {
      this.moduleList = this._.cloneDeep(MODULE_DATA.filter(mods => { return mods.type === type && !mods.hide }))
    },
    onChange: function (d) {
      this.$emit('update', this.moduleData)
      // 텍스트모듈로 drop시 문제 해결할것
      // console.log(d)
    },
    onCheckQuantityEnter: function (e) {
      const isQuantity = e.target.dataset['mods_name'] === 'quantity'
      this.blockQuantity = this.hasQuantity && isQuantity
    },
    onCheckQuantityLeave: function (e) {
      this.blockQuantity = false
    },
    setHide: function (e, index) {
      const display = e.target.checked
      const data = this.moduleData[index]
      data.hide = display
    },
    copyToDevice: function (index) {
      const device = this.responsive === 'tablet' ? 'mobile' : 'tablet'
      const copyto = `module_copyto_${device}`
      const confirm = this.locale[copyto.toLowerCase()]
      // 이 모듈을 태블릿||모바일로 복사합니다
      if (window.confirm(confirm)) {
        const copyTo = this.responsive === 'tablet' ? 'mobileModule' : 'tabletModule'
        const data = this._.cloneDeep(this.moduleData[index])
        data.id = UNIQUE.mid()
        this.$emit('copyTo', copyTo, data)
      }
    },
    onClone: function (data) {
      const cloneData = this._.cloneDeep(data)
      cloneData.id = UNIQUE.mid()
      return cloneData
    },
    onRemove: function (e, index) {
      e.stopPropagation()
      if (confirm(this.locale.confirm_delete_module)) {
        this.moduleData.splice(index, 1)
      }
    },
    onCopy: function (e, index, mods) {
      e.stopPropagation()
      if (mods.name !== 'quantity') {
        const data = this.moduleData[index]
        const cloneData = this._.cloneDeep(data)
        const id = UNIQUE.mid()
        const optionName = /radio|checkbox|select/
        // 서브옵션은 해제해서 복사
        if (cloneData.setting.action && optionName.test(cloneData.setting.action.name) && cloneData.setting.action.item.find(x => x.subOption)) {
          for (let i = 0; i < cloneData.setting.action.item.length; i++) {
            delete cloneData.setting.action.item[i].subOption
          }
        }
        cloneData.id = id
        // delete clonData.
        this.moduleData.splice(index + 1, 0, cloneData)
      } else {
        alert(this.locale.validate_quantity_limit)
      }
    },
    /**
     * 특정 엘리먼트에 클래스명을 추가하고 인접 엘리먼트들에 클래스명을 삭제
     * @param target 클래스 추가할 대상, DOM object (* array 아님 주의)
     * @param classname 추가/삭제할 클래스명, string
     * @param classaction add or remove 단일 실행
     */
    addRemoveClass: function (target, classname, classaction) {
      if (classaction !== 'add') {
        [...target.parentElement.children].forEach(el => el.classList.remove(classname))
      }
      if (classaction !== 'remove') {
        target.classList.add(classname)
      }
    },
    onMouseEnter: function (e) {
      e.target.classList.add('hover')
    },
    onMouseLeave: function (e) {
      e.target.classList.remove('hover')
    },
    onModuleClick: function (e, index, data) {
      e.stopPropagation()
      let target = e.currentTarget
      if (e.target.closest('.mods') !== null && e.target.closest('.mods').className.indexOf('active') === -1) {
        this.addRemoveClass(target, 'active')
        this.isActive = true
        this.activeModule = data
        this.activeTarget = 'mods'
      }
      const setbox = document.querySelectorAll('.setbox')
      setbox.forEach(el => el.classList.remove('active'))
    },
    onDocumentClick: function (e) {
      if (e.target.closest('.article') === null && e.target.closest('.handleMods') === null) {
        const mods = document.querySelector('.mods')
        const column = document.querySelectorAll('.column')
        if (mods !== null) {
          this.addRemoveClass(mods, 'active', 'remove')
          this.isActive = false
        }
        if (column !== null) {
          column.forEach(el => el.classList.remove('active'))
        }
      }
    },
    modsDisplay: function (display) {
      const responsive = this.responsive
      return display ? display[responsive] ? 'show' : 'hide' : 'show'
    },
    onAddOption: function (data) {
      const newOption = this._.cloneDeep(data.itemDefault)
      data.item.push(newOption)
    },
    onRemoveOption: function (data) {
      if (window.confirm(this.locale.confirm_delete_option)) {
        // 기본옵션으로 체크된 옵션?
        if (data.action && data.action.defaultValue) {
          if (data.action.name === 'radio' && data.option.find((x, i) => x.id || `${i}_${x.value}` === data.action.defaultValue)) {
            data.action.defaultValue = ''
          } else if (data.action.name === 'checkbox' && data.option.find((x, i) => data.action.defaultValue.includes(x.id || `${i}_${x.value}`))) {
            data.action.defaultValue = data.action.defaultValue.filter(x => {
              return data.option.find((o, i) => x === (o.id || `${i}_${o.value}`))
            })
          }
        }
        data.option.splice(data.index, 1)
      }
    },
    onCheckedPrice: function (isChecked) {
      // this.checkedPrice = isChecked
    },
    onSetDetail: function (detail) {
      this.activeColumn = detail
      this.activeTarget = 'column'
    },
    onChangeColumn: function (data) {
      this.$emit('changeColumn', data)
    }
  }
}
</script>
